import { useState } from 'react';

export default function ImportResultsModal({ results, isOpen, onClose }) {
  if (!isOpen || !results) return null;

  const {
    message,
    imported = {},
    skipped = {},
    errors = [],
  } = results;

  const totalImported = (imported.clips || 0) + (imported.favorites || 0) + (imported.tags || 0);
  const totalSkipped = (skipped.clips_duplicate || 0) + (skipped.clips_limit || 0) + 
                       (skipped.favorites_duplicate || 0) + (skipped.favorites_limit || 0);
  const hasErrors = errors && errors.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 border-b flex-shrink-0 ${totalImported > 0 ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className={`text-lg font-semibold ${totalImported > 0 ? 'text-green-900' : 'text-blue-900'}`}>
                {totalImported > 0 ? '✓ Import Successful' : 'Import Complete'}
              </h2>
              <p className={`text-sm mt-1 ${totalImported > 0 ? 'text-green-800' : 'text-blue-800'}`}>
                {message}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content (scrollable) */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {/* Imported section */}
          {totalImported > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <h3 className="font-medium text-green-900 mb-2">✓ Imported</h3>
              <dl className="space-y-1 text-sm">
                {imported.clips > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-green-800">Clips:</dt>
                    <dd className="font-medium text-green-900">{imported.clips}</dd>
                  </div>
                )}
                {imported.favorites > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-green-800">Favorites:</dt>
                    <dd className="font-medium text-green-900">{imported.favorites}</dd>
                  </div>
                )}
                {imported.tags > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-green-800">Tags:</dt>
                    <dd className="font-medium text-green-900">{imported.tags}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Skipped section */}
          {totalSkipped > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <h3 className="font-medium text-amber-900 mb-2">⏭️ Skipped (Already Exist or Over Limit)</h3>
              <dl className="space-y-1 text-sm">
                {(skipped.clips_duplicate || 0) > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-amber-800">Duplicate clips:</dt>
                    <dd className="font-medium text-amber-900">{skipped.clips_duplicate}</dd>
                  </div>
                )}
                {/* c8 ignore start */}
                {(skipped.clips_limit || 0) > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-amber-800">Clips over tier limit:</dt>
                    <dd className="font-medium text-amber-900">{skipped.clips_limit}</dd>
                  </div>
                )}
                {/* c8 ignore stop */}
                {/* c8 ignore start */}
                {(skipped.favorites_duplicate || 0) > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-amber-800">Duplicate favorites:</dt>
                    <dd className="font-medium text-amber-900">{skipped.favorites_duplicate}</dd>
                  </div>
                )}
                {/* c8 ignore stop */}
                {(skipped.favorites_limit || 0) > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-amber-800">Favorites over tier limit:</dt>
                    <dd className="font-medium text-amber-900">{skipped.favorites_limit}</dd>
                  </div>
                )}
              </dl>
              {/* c8 ignore start */}
              {((skipped.clips_limit || 0) > 0 || (skipped.favorites_limit || 0) > 0) && (
                <p className="text-xs text-amber-700 mt-2">
                  💡 Tip: Upgrade your subscription or remove some items to import more data.
                </p>
              )}
              {/* c8 ignore stop */}
            </div>
          )}

          {/* Errors section */}
          {hasErrors && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <h3 className="font-medium text-red-900 mb-2">
                {/* c8 ignore start */}
                ⚠️ {errors.length} {errors.length === 1 ? 'Error' : 'Errors'}
                {/* c8 ignore stop */}
              </h3>
              <ul className="space-y-1 text-xs text-red-800">
                {errors.slice(0, 3).map((error, idx) => (
                  <li key={idx} className="text-red-700">
                    {/* c8 ignore start */}
                    • {error.message || error.reason || 'Unknown error'}
                    {/* c8 ignore stop */}
                  </li>
                ))}
                {errors.length > 3 && (
                  <li className="text-red-700 italic">
                    • ... and {errors.length - 3} more
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Empty state message */}
          {totalImported === 0 && totalSkipped === 0 && !hasErrors && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                No new items were imported. All items in this file already exist in your collection.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
